import { memo } from "react";
import { Button } from "primereact/button";

import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { FEATURES } from "sj-ui-toolkit/src/constants/features";
import type { SectionExamData } from "sj-ui-toolkit/src/api/userInitiatedExams";

import { PROFICIENCY_THRESHOLDS } from "./assessmentHelpers";
import { ScoreHistoryChart } from "./ScoreHistoryChart";
import { CompetencyScore } from "./CompetencyScore";
import { TopicList } from "./TopicList";
import S from "./Assessments.module.scss";

interface SectionDetailProps {
  sectionData: SectionExamData;
  onRetake: (sectionPath: string) => void;
}

/** Memoized detailed results card for a section that has been taken */
const SectionDetail = memo(({ sectionData, onRetake }: SectionDetailProps) => {
  const {
    section,
    latestScorePercent,
    latestTeamAveragePercent,
    timesTaken,
    scores,
    topicScores,
    inProgressExam,
  } = sectionData;
  const { isFeatureEnabled } = useSettingsContext();
  const userInitiatedExams = isFeatureEnabled(FEATURES.SELF_ASSESSMENTS);

  const proficiencies = topicScores.filter(
    t => (t.scorePercent ?? 0) >= PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES
  );
  const growthOpportunities = topicScores.filter(
    t => (t.scorePercent ?? 0) < PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES
  );

  const timesLabel = timesTaken === 1 ? "Taken once" : `Taken ${timesTaken} times`;

  const headingId = `section-heading-${section.path}`;

  return (
    <article aria-labelledby={headingId} className="border-t mt-12 pt-4 border-surface-300">
      <h3 id={headingId} className={`atlas-h2 ${S.sectionHeader}`}>
        {section.title}
      </h3>
      <div className="text-muted-aaa body-sm">{timesLabel}</div>

      <div className={S.sectionContent}>
        <ScoreHistoryChart scores={scores} />
        <div>
          <CompetencyScore
            latestScorePercent={latestScorePercent}
            latestTeamAveragePercent={latestTeamAveragePercent}
          />
          {userInitiatedExams && (
            <Button
              label={inProgressExam ? "Continue Assessment" : "Retake Assessment"}
              outlined
              severity="secondary"
              className="mt-6"
              onClick={() => onRetake(section.path)}
              aria-label={`${inProgressExam ? "Continue" : "Retake"} ${section.title} assessment`}
            />
          )}
        </div>
        <TopicList heading="Proficiencies" topics={proficiencies} variant="proficiency" />
        <TopicList heading="Growth Opportunities" topics={growthOpportunities} variant="growth" />
      </div>
    </article>
  );
});

SectionDetail.displayName = "SectionDetail";

export { SectionDetail };
